import { Flex, Text } from "@mantine/core";
import get from "lodash/get";

import SvgAlert from "../../../components/Icons/Alert";
import SvgCheckCircle from "../../../components/Icons/CheckCircle.tsx";
import SvgCloseCircle from "../../../components/Icons/CloseCircle.tsx";
import { theme } from "../../../utils/mantine/theme.ts";
import { LoadingMessage } from "../LoadingMessage.tsx";
import { ForgeryResult, ForgeryStatus } from "../types";
import { UploadDocumentMessage } from "../UploadDocumentMessage.tsx";
import { useStyles } from "./ForgeryResultDisplay.styles";

interface ForgeryResultDisplayProps {
  result: ForgeryResult | null;
  loading: boolean;
  isProccesingFailed?: boolean;
}

const ForgeryResultDisplay = ({
  result,
  loading,
  isProccesingFailed,
}: ForgeryResultDisplayProps) => {
  const { classes } = useStyles();

  const greenColor = get(theme, "colors.green.0", "green");
  const redColor = get(theme, "colors.red.0", "red");

  if (isProccesingFailed) {
    return (
      <Flex direction="column" align="center" gap={16} pt={16}>
        <SvgAlert width={80} height={80} color={redColor} />
        <Text align="center">DOCUMENT PROCESSING FAILED</Text>
        <Text size="sm" align="center" color="dimmed">
          Please try uploading the document again or contact support if the
          issue persists
        </Text>
      </Flex>
    );
  }

  if (loading) {
    return <LoadingMessage />;
  }

  if (!result) {
    return <UploadDocumentMessage />;
  }

  const isForged = result.status === ForgeryStatus.FORGED;
  const statusIcon = isForged ? (
    <SvgAlert width={60} height={60} color={redColor} />
  ) : (
    <SvgCheckCircle width={60} height={60} color={greenColor} />
  );

  return (
    <Flex direction="column" align="center" gap={16} pt={16}>
      <Flex direction="column" align="center" w="60%" gap={24}>
        {statusIcon}
        <Text size="lg" align="center">
          This document {isForged ? "has a possibility of " : "is "}
          <Text
            weight={700}
            className={
              isForged ? classes.statusTextForged : classes.statusTextValid
            }
          >
            {isForged ? "forgery" : "valid, forgery not detected"}.
          </Text>
        </Text>

        <Flex direction="column" w="100%" gap={16}>
          {result.checks.map((check, index) => (
            <Flex
              key={index}
              className={
                check.passed ? classes.checkItemPassed : classes.checkItemFailed
              }
              align="center"
            >
              {check.passed ? (
                <SvgCheckCircle color={greenColor} />
              ) : (
                <SvgCloseCircle color={redColor} />
              )}
              <Text>{check.type}</Text>
            </Flex>
          ))}
        </Flex>
      </Flex>
    </Flex>
  );
};

export default ForgeryResultDisplay;
